"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";

type Direction = "auto" | "to-morse" | "to-text";

const MORSE_MAP: Record<string, string> = {
  A: ".-",
  B: "-...",
  C: "-.-.",
  D: "-..",
  E: ".",
  F: "..-.",
  G: "--.",
  H: "....",
  I: "..",
  J: ".---",
  K: "-.-",
  L: ".-..",
  M: "--",
  N: "-.",
  O: "---",
  P: ".--.",
  Q: "--.-",
  R: ".-.",
  S: "...",
  T: "-",
  U: "..-",
  V: "...-",
  W: ".--",
  X: "-..-",
  Y: "-.--",
  Z: "--..",
  "0": "-----",
  "1": ".----",
  "2": "..---",
  "3": "...--",
  "4": "....-",
  "5": ".....",
  "6": "-....",
  "7": "--...",
  "8": "---..",
  "9": "----.",
  ".": ".-.-.-",
  ",": "--..--",
  "?": "..--..",
  "'": ".----.",
  "!": "-.-.--",
  "/": "-..-.",
  "(": "-.--.",
  ")": "-.--.-",
  "&": ".-...",
  ":": "---...",
  ";": "-.-.-.",
  "=": "-...-",
  "+": ".-.-.",
  "-": "-....-",
  _: "..--.-",
  '"': ".-..-.",
  $: "...-..-",
  "@": ".--.-.",
};

const REVERSE_MORSE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(MORSE_MAP).map(([char, code]) => [code, char])
);

const MORSE_ONLY_REGEX = /^[.\-/\s]+$/;
const WHITESPACE_REGEX = /\s+/;
const WORD_SPLIT_REGEX = /\s*\/\s*/;

const REFERENCE_CHARS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
];

function encodeWord(word: string, unknown: Set<string>): string {
  const codes: string[] = [];
  for (const char of word.toUpperCase()) {
    const code = MORSE_MAP[char];
    if (code === undefined) {
      unknown.add(char);
    } else {
      codes.push(code);
    }
  }
  return codes.join(" ");
}

function encodeToMorse(text: string): { output: string; unknown: string[] } {
  const unknown = new Set<string>();
  const words = text.trim().split(WHITESPACE_REGEX);
  const encoded = words
    .map((word) => encodeWord(word, unknown))
    .filter((word) => word.length > 0);
  return { output: encoded.join(" / "), unknown: [...unknown] };
}

function decodeWord(word: string, unknown: Set<string>): string {
  const letters: string[] = [];
  for (const code of word.trim().split(WHITESPACE_REGEX)) {
    if (code.length === 0) {
      continue;
    }
    const char = REVERSE_MORSE_MAP[code];
    if (char === undefined) {
      unknown.add(code);
    } else {
      letters.push(char);
    }
  }
  return letters.join("");
}

function decodeFromMorse(morse: string): { output: string; unknown: string[] } {
  const unknown = new Set<string>();
  const words = morse.trim().split(WORD_SPLIT_REGEX);
  const decoded = words.map((word) => decodeWord(word, unknown));
  return { output: decoded.join(" ").trim(), unknown: [...unknown] };
}

function isMorseInput(text: string): boolean {
  return MORSE_ONLY_REGEX.test(text.trim());
}

function translate(
  input: string,
  direction: Direction
): {
  output: string;
  unknown: string[];
  decoded: boolean;
} {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { output: "", unknown: [], decoded: false };
  }
  const shouldDecode =
    direction === "to-text" || (direction === "auto" && isMorseInput(trimmed));
  if (shouldDecode) {
    return { ...decodeFromMorse(trimmed), decoded: true };
  }
  return { ...encodeToMorse(trimmed), decoded: false };
}

/** English strings. A translated content file overrides these through `ui`. */
export const MORSE_CODE_TRANSLATOR_STRINGS = {
  direction: "Direction",
  autoDetect: "Auto-detect",
  textToMorse: "Text → Morse",
  morseToText: "Morse → Text",
  inputLabel: "Text or Morse code",
  inputPlaceholder:
    "Hello world  —or—  .... . .-.. .-.. --- / .-- --- .-. .-.. -..",
  decodedText: "Decoded text",
  morseCode: "Morse code",
  unrecognizedMorseSequences: "Unrecognized Morse sequences",
  unsupportedCharacters: "Unsupported characters",
  skippedList: "{what} skipped: {list}",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  morseCodeChart: "Morse Code Chart",
} as const;

export default function MorseCodeTranslatorWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<Direction>("auto");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(MORSE_CODE_TRANSLATOR_STRINGS);

  const DIRECTIONS: { value: Direction; label: string }[] = [
    { value: "auto", label: strings.autoDetect },
    { value: "to-morse", label: strings.textToMorse },
    { value: "to-text", label: strings.morseToText },
  ];

  const { output, unknown, decoded } = translate(input, direction);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Direction */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.direction}
        </p>
        <div className="flex flex-wrap gap-3">
          {DIRECTIONS.map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d.value}
            >
              <input
                checked={direction === d.value}
                className="accent-primary"
                name="direction"
                onChange={() => setDirection(d.value)}
                type="radio"
                value={d.value}
              />
              {d.label}
            </label>
          ))}
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="morse-input"
        >
          {strings.inputLabel}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="morse-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="morse-output"
          >
            {decoded ? strings.decodedText : strings.morseCode}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="morse-output"
            readOnly
            value={output}
          />
        </div>
      )}
      {unknown.length > 0 && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
          {t(strings.skippedList, {
            what: decoded
              ? strings.unrecognizedMorseSequences
              : strings.unsupportedCharacters,
            list: unknown.join(", "),
          })}
        </p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Reference chart */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.morseCodeChart}
        </h2>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {REFERENCE_CHARS.map((char) => (
            <div
              className="rounded-lg border border-border bg-card p-3 text-center"
              key={char}
            >
              <p className="font-semibold text-foreground">{char}</p>
              <p className="mt-1 font-mono text-muted-foreground text-xs">
                {MORSE_MAP[char]}
              </p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
